/**
 * Surveillance targets store — live MLAT/ADS-B/Vision fused aircraft.
 */
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type { Target, TargetsEnvelope } from '@/lib/mqtt/schemas/targets';

const TRAIL_MAX = 60;
/** Grace window past `received_at` before a target is evicted from
 *  byId. Sized to outlast the worst-case broker hiccup observed on
 *  staging — keepalive-timeout-driven WSS reconnects can drop us for
 *  5–10 s, after which slow-cadence emitters (live_regional_*) take up
 *  to another 20 s to republish; without enough grace, aircraft from
 *  those feeds visibly vanish then reappear. 60 s covers it with
 *  headroom while still cleaning up genuinely-departed aircraft within
 *  a minute. */
const MAX_STALE_MS = 60_000;

interface TargetsStore {
  byId: Record<string, Target>;
  trails: Record<string, [number, number][]>;
  ackedIds: Set<string>;
  selectedId: string | null;
  applyTargets: (env: TargetsEnvelope) => void;
  acknowledge: (id: string) => void;
  select: (id: string | null) => void;
}

export const useTargetsStore = create<TargetsStore>()(
  devtools(
    subscribeWithSelector((set) => ({
      byId: {},
      trails: {},
      ackedIds: new Set(),
      selectedId: null,
      applyTargets: (env) =>
        set((s) => {
          // Merge into existing byId rather than rebuilding from scratch.
          // A 1 Hz envelope that shrinks (because the source aged some
          // targets out, or because a broker hiccup is in progress) must
          // not wipe targets that simply weren't republished this tick —
          // it took us a few iterations to track down "all planes vanish
          // for a few seconds then come back" as exactly this. The
          // explicit `received_at` check below handles permanent removal.
          const next = { ...s.byId };
          const trails = { ...s.trails };
          const refreshed = new Set<string>();
          for (const t of env.targets) {
            next[t.target_id] = s.ackedIds.has(t.target_id) ? { ...t, proximity: 'acknowledged' } : t;
            refreshed.add(t.target_id);
            const prevTrail = trails[t.target_id] ?? [];
            const last = prevTrail[prevTrail.length - 1];
            if (!last || last[0] !== t.lng || last[1] !== t.lat) {
              const nt = prevTrail.slice();
              nt.push([t.lng, t.lat]);
              if (nt.length > TRAIL_MAX) nt.splice(0, nt.length - TRAIL_MAX);
              trails[t.target_id] = nt;
            }
          }
          // Evict stale ghosts — targets the source stopped publishing
          // more than MAX_STALE_MS ago. Anything refreshed this tick is
          // exempt regardless of its previous timestamp.
          const cutoffMs = Date.now() - MAX_STALE_MS;
          const evicted: string[] = [];
          for (const id of Object.keys(next)) {
            if (refreshed.has(id)) continue;
            const r = next[id]?.received_at;
            const at = r ? Date.parse(r) : 0;
            if (!Number.isFinite(at) || at < cutoffMs) {
              evicted.push(id);
              delete next[id];
              delete trails[id];
            }
          }
          // Opt-in diagnostic — set `window.__DEBUG_TARGETS_STORE__ = true`
          // in DevTools console to watch every envelope. Helps verify the
          // merge fix is actually loaded and not being undone elsewhere.
          if (typeof window !== 'undefined' &&
              (window as unknown as { __DEBUG_TARGETS_STORE__?: boolean }).__DEBUG_TARGETS_STORE__) {
            const prevCount = Object.keys(s.byId).length;
            const newCount = Object.keys(next).length;
            // eslint-disable-next-line no-console
            console.log(
              `[targets.applyTargets] env=${env.targets.length}  prev=${prevCount}  now=${newCount}` +
              (evicted.length ? `  evicted=${evicted.length}: ${evicted.slice(0, 5).join(',')}${evicted.length > 5 ? '…' : ''}` : ''),
            );
          }
          return { byId: next, trails };
        }),
      acknowledge: (id) =>
        set((s) => {
          const ack = new Set(s.ackedIds);
          ack.add(id);
          const t = s.byId[id];
          return {
            ackedIds: ack,
            byId: t ? { ...s.byId, [id]: { ...t, proximity: 'acknowledged' } } : s.byId,
          };
        }),
      select: (id) => set({ selectedId: id }),
    })),
    { name: 'targets' },
  ),
);
